/**
 * WhatsApp Gateway Service Client
 * All operations are branch-scoped — branchId is required for every call.
 */

import axios from 'axios';

const WA_BASE_URL = import.meta.env.VITE_WHATSAPP_URL || 'http://localhost:9016';
const WA_API_KEY = import.meta.env.VITE_WHATSAPP_API_KEY || '';

const waClient = axios.create({
  baseURL: WA_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    ...(WA_API_KEY ? { 'x-api-key': WA_API_KEY } : {}),
  },
});

const whatsappService = {
  /** Health check (global) */
  getHealth: () => waClient.get('/health').then(r => r.data),

  /** List all branch sessions (global) */
  getSessions: () => waClient.get('/sessions').then(r => r.data),

  // ── Branch-scoped endpoints ──────────────────────────────────────────

  /** Get connection status for a branch */
  getStatus: (branchId) =>
    waClient.get(`/${branchId}/status`).then(r => r.data),

  /** Get QR code as base64 data URL for a branch */
  getQR: (branchId) =>
    waClient.get(`/${branchId}/qr`).then(r => r.data),

  /** Get QR code as image URL (for <img> tag) */
  getQRImageUrl: (branchId) =>
    `${WA_BASE_URL}/${branchId}/qr?format=image&t=${Date.now()}`,

  /** Send a WhatsApp message from a branch */
  send: (branchId, phone, message) =>
    waClient.post(`/${branchId}/send`, { phone, message }).then(r => r.data),

  /** Check if a number is on WhatsApp */
  checkNumber: (branchId, phone) =>
    waClient.get(`/${branchId}/check/${phone.replace(/\D/g, '')}`).then(r => r.data),

  /** Get recent message logs for a branch */
  getLogs: (branchId, limit = 20) =>
    waClient.get(`/${branchId}/logs?limit=${limit}`).then(r => r.data),

  /** Disconnect / logout WhatsApp for a branch */
  logout: (branchId) =>
    waClient.post(`/${branchId}/logout`).then(r => r.data),

  /** Force reconnect for a branch */
  reconnect: (branchId) =>
    waClient.post(`/${branchId}/reconnect`).then(r => r.data),
};

export default whatsappService;
